import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Avatar, { toInitials } from './ui/Avatar';
import Btn from './ui/Btn';
import CounterCircle from './ui/CounterCircle';
import DictationButton from './DictationButton';
import { ApiError, api } from '../lib/api';
import { downloadBlob } from '../lib/download';
import { noteScopeMime } from '../lib/dragDrop';
import { pieceLabel, pieceRefLine } from '../lib/pieceRef';
import { toast } from '../stores/toaster';
import {
  Annotation,
  AnnotationCategory,
  DossierNote,
  Partie,
  PartieRole,
  PdfMetadata,
  Tag,
} from '../lib/types';

/**
 * Vue « Doc travail » à l'échelle du dossier (Sprint 1ter+/++ /
 * ADR-031 + ADR-032).
 *
 * Liste fusionnée annotations + notes triée DANS L'ORDRE DES DOCUMENTS
 * (pièce → page → sort_key) — pas dans l'ordre chronologique d'action
 * de l'utilisateur. La référence exacte de la pièce est affichée en
 * évidence sous chaque extrait.
 */

const CAT_META: Record<
  AnnotationCategory,
  { label: string; cardClass: string; labelClass: string }
> = {
  favorable: { label: 'FAVORABLE', cardClass: 'border-l-cat-fav', labelClass: 'text-cat-fav' },
  defavorable: { label: 'DÉFAVORABLE', cardClass: 'border-l-cat-def', labelClass: 'text-cat-def' },
  important: { label: 'IMPORTANT', cardClass: 'border-l-cat-imp', labelClass: 'text-cat-imp-text' },
  a_verifier: { label: 'À VÉRIFIER', cardClass: 'border-l-cat-ver', labelClass: 'text-cat-ver' },
};

const ROLE_LABELS_SHORT: Record<PartieRole, string> = {
  prevenu: 'prévenu',
  partie_civile: 'partie civile',
  victime: 'victime',
  temoin: 'témoin',
  enqueteur: 'enquêteur',
  expert: 'expert',
  autre: 'autre',
};

type Item = { kind: 'annotation'; data: Annotation } | { kind: 'note'; data: DossierNote };

function relativeAge(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}

// pieceLabel + pieceRefLine déplacés dans lib/pieceRef.ts (Sprint 3+)
// pour partage avec RechercheTab + AudiencePage. Le format strippe le
// .pdf final du filename_original quand pas de titre_pv plus parlant.

type Props = {
  dossierId: string;
  pdfs: PdfMetadata[];
  annotations: Annotation[];
  onAnnotationsChange: () => Promise<void>;
};

type FilterPartyMode = 'all' | string; // partie UUID or 'all'

export default function AnnotationsCitationsTab({
  dossierId,
  pdfs,
  annotations,
  onAnnotationsChange,
}: Props) {
  const [notes, setNotes] = useState<DossierNote[]>([]);
  const [parties, setParties] = useState<Partie[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterCat, setFilterCat] = useState<AnnotationCategory | 'all'>('all');
  // U6 — filtre rapide « non rouvertes >7j » (annotations non modifiées
  // depuis 7 jours, donc potentiellement à relire/préciser).
  const [filterStale7d, setFilterStale7d] = useState(false);
  const [filterPdfId, setFilterPdfId] = useState<string | 'all'>('all');
  // ADR-064 raffinement : filtre piece_client séparé du filtre pdf_file (la
  // colonne en DB est XOR — un cliché simultané ne fait pas sens).
  const [filterPieceClientId, setFilterPieceClientId] = useState<string | 'all'>('all');
  const [piecesClient, setPiecesClient] = useState<
    { id: string; titre: string; date_piece?: string | null }[]
  >([]);
  const [filterAuthor, setFilterAuthor] = useState<string | 'all'>('all');
  const [filterParty, setFilterParty] = useState<FilterPartyMode>('all');
  const [filterTag, setFilterTag] = useState<FilterPartyMode>('all');
  const [search, setSearch] = useState<string>('');
  const [dossierNoteDraft, setDossierNoteDraft] = useState<string | null>(null);

  // Bulk selection (ADR-061).
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMenu, setBulkMenu] = useState<null | 'category' | 'parties' | 'tags' | 'delete'>(null);
  // Sprint 2 §8 — export PDF du Doc travail.
  const [exporting, setExporting] = useState(false);

  const exportNotesPdf = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectionMode && selectedIds.size > 0) {
        // L'export filtre par annotation_ids ; les notes ne sont pas
        // selectionnables dans le mode bulk actuel (id-only set
        // d'annotations). Pour ne pas mélanger, on omet les notes
        // quand l'utilisateur a fait une selection explicite.
        params.set('annotation_ids', Array.from(selectedIds).join(','));
        params.set('note_ids', '');
      }
      const qs = params.toString();
      await downloadBlob(
        `/api/v1/dossiers/${dossierId}/export-notes-pdf${qs ? `?${qs}` : ''}`,
        `doc-travail-${dossierId.slice(0, 8)}.pdf`
      );
      toast.success('Doc travail exporté en PDF');
    } catch (err) {
      toast.error((err as Error).message || 'Erreur lors de l’export');
    } finally {
      setExporting(false);
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const exitSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setBulkMenu(null);
  };

  const loadNotes = async () => {
    setLoading(true);
    try {
      const [ns, pts, tgs, pcs] = await Promise.all([
        api.get<DossierNote[]>(`/dossiers/${dossierId}/notes`),
        api.get<Partie[]>(`/dossiers/${dossierId}/parties`),
        api.get<Tag[]>(`/dossiers/${dossierId}/tags`),
        api.get<{ id: string; titre: string; date_piece?: string | null }[]>(
          `/dossiers/${dossierId}/pieces-client`
        ),
      ]);
      setNotes(ns);
      setParties(pts);
      setTags(tgs);
      setPiecesClient(pcs);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const partyById = useMemo(() => {
    const m = new Map<string, Partie>();
    for (const p of parties) m.set(p.id, p);
    return m;
  }, [parties]);

  const tagById = useMemo(() => {
    const m = new Map<string, Tag>();
    for (const t of tags) m.set(t.id, t);
    return m;
  }, [tags]);

  const updatePartyLinks = async (annotationId: string, partyIds: string[]) => {
    await api.patch<Annotation>(`/annotations/${annotationId}`, { partie_ids: partyIds });
    await onAnnotationsChange();
  };

  const updateTagLinks = async (annotationId: string, tagIds: string[]) => {
    await api.patch<Annotation>(`/annotations/${annotationId}`, { tag_ids: tagIds });
    await onAnnotationsChange();
  };

  const reloadTags = async () => {
    const rows = await api.get<Tag[]>(`/dossiers/${dossierId}/tags`);
    setTags(rows);
  };

  const createAndLinkTag = async (
    annotationId: string,
    label: string,
    color?: string
  ): Promise<string | null> => {
    try {
      const body: Record<string, unknown> = { label };
      if (color) body.color = color;
      const tag = await api.post<Tag>(`/dossiers/${dossierId}/tags`, body);
      await reloadTags();
      const ann = annotations.find((a) => a.id === annotationId);
      const next = [...(ann?.linked_tag_ids ?? []), tag.id];
      await updateTagLinks(annotationId, next);
      return tag.id;
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
      return null;
    }
  };

  const updateComment = async (annotationId: string, newComment: string) => {
    await api.patch<Annotation>(`/annotations/${annotationId}`, {
      comment: newComment,
    });
    await onAnnotationsChange();
  };

  const updateCategory = async (annotationId: string, newCategory: AnnotationCategory) => {
    await api.patch<Annotation>(`/annotations/${annotationId}`, {
      category: newCategory,
    });
    await onAnnotationsChange();
  };

  // ---- Bulk operations (ADR-061) ----------------------------------------
  // N parallel PATCH/DELETE requests. No dedicated backend endpoint —
  // keeps the diff small and lets the existing audit hooks fire per row.

  const annotationById = useMemo(() => {
    const m = new Map<string, Annotation>();
    for (const a of annotations) m.set(a.id, a);
    return m;
  }, [annotations]);

  const bulkSetCategory = async (cat: AnnotationCategory) => {
    setBulkBusy(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          api.patch<Annotation>(`/annotations/${id}`, { category: cat })
        )
      );
      toast.success(`${selectedIds.size} annotation(s) recatégorisée(s).`);
      exitSelection();
      await onAnnotationsChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkAddParty = async (partyId: string) => {
    setBulkBusy(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => {
          const a = annotationById.get(id);
          if (!a || a.linked_party_ids.includes(partyId)) return Promise.resolve();
          return api.patch<Annotation>(`/annotations/${id}`, {
            partie_ids: [...a.linked_party_ids, partyId],
          });
        })
      );
      toast.success(`Partie liée à ${selectedIds.size} annotation(s).`);
      exitSelection();
      await onAnnotationsChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkAddTag = async (tagId: string) => {
    setBulkBusy(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => {
          const a = annotationById.get(id);
          if (!a || a.linked_tag_ids.includes(tagId)) return Promise.resolve();
          return api.patch<Annotation>(`/annotations/${id}`, {
            tag_ids: [...a.linked_tag_ids, tagId],
          });
        })
      );
      toast.success(`Tag ajouté à ${selectedIds.size} annotation(s).`);
      exitSelection();
      await onAnnotationsChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => api.delete(`/annotations/${id}`)));
      toast.success(`${selectedIds.size} annotation(s) supprimée(s).`);
      exitSelection();
      await onAnnotationsChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const pdfById = useMemo(() => {
    const m = new Map<string, PdfMetadata>();
    for (const p of pdfs) m.set(p.id, p);
    return m;
  }, [pdfs]);

  // Piece order = upload order (primary) then filename (tiebreak). Each
  // PDF gets an integer rank; we use it as the first sort key so the
  // working document follows the dossier's structure rather than the
  // user's reading sequence.
  const pieceRank = useMemo(() => {
    const sorted = [...pdfs].sort((a, b) => {
      const ta = new Date(a.uploaded_at).getTime();
      const tb = new Date(b.uploaded_at).getTime();
      if (ta !== tb) return ta - tb;
      return a.filename_original.localeCompare(b.filename_original);
    });
    const m = new Map<string, number>();
    sorted.forEach((p, idx) => m.set(p.id, idx));
    return m;
  }, [pdfs]);

  const searchNeedle = search.trim().toLocaleLowerCase('fr');

  const filteredAnnotations = useMemo(
    () =>
      annotations.filter((a) => {
        if (filterCat !== 'all' && a.category !== filterCat) return false;
        if (filterPdfId !== 'all' && a.pdf_file_id !== filterPdfId) return false;
        if (filterPieceClientId !== 'all' && a.piece_client_id !== filterPieceClientId)
          return false;
        if (filterAuthor !== 'all' && (a.creator_display_name ?? '') !== filterAuthor) return false;
        if (filterParty !== 'all') {
          if (filterParty === '__none__') {
            if (a.linked_party_ids.length > 0) return false;
          } else if (!a.linked_party_ids.includes(filterParty)) {
            return false;
          }
        }
        if (filterTag !== 'all') {
          if (filterTag === '__none__') {
            if (a.linked_tag_ids.length > 0) return false;
          } else if (!a.linked_tag_ids.includes(filterTag)) {
            return false;
          }
        }
        if (searchNeedle) {
          const hay = `${a.text_extract} ${a.comment ?? ''}`.toLocaleLowerCase('fr');
          if (!hay.includes(searchNeedle)) return false;
        }
        if (filterStale7d) {
          const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
          const updated = new Date(a.updated_at).getTime();
          if (Date.now() - updated < sevenDaysMs) return false;
        }
        return true;
      }),
    [
      annotations,
      filterCat,
      filterPdfId,
      filterPieceClientId,
      filterAuthor,
      filterParty,
      filterTag,
      searchNeedle,
      filterStale7d,
    ]
  );

  // Note filter: piece-scoped notes always travel with their piece;
  // dossier-wide notes (pdf_file_id null) only show when "all pieces"
  // is selected and don't have a category to filter on.
  const filteredNotes = useMemo(
    () =>
      notes.filter((n) => {
        if (filterCat !== 'all') return false; // notes have no category
        if (filterPdfId !== 'all' && n.pdf_file_id !== filterPdfId) return false;
        if (filterPieceClientId !== 'all' && n.piece_client_id !== filterPieceClientId)
          return false;
        if (filterAuthor !== 'all' && (n.creator_display_name ?? '') !== filterAuthor) return false;
        if (filterParty !== 'all') return false; // notes don't link to parties
        if (filterTag !== 'all') return false; // notes don't carry tags
        if (searchNeedle) {
          if (!n.content.toLocaleLowerCase('fr').includes(searchNeedle)) return false;
        }
        return true;
      }),
    [
      notes,
      filterCat,
      filterPdfId,
      filterPieceClientId,
      filterAuthor,
      filterParty,
      filterTag,
      searchNeedle,
    ]
  );

  // ---- Sort tuple ------------------------------------------------------
  // Items are sorted by:
  //   1. piece rank (NULL = -1, sits before all real pieces)
  //   2. page number (NULL = 0)
  //   3. sort_key (notes) or 0 (annotations) so notes pinned to a page
  //      land before that page's annotations
  //   4. created_at as a stable tiebreaker
  const sortTuple = (
    pdfFileId: string | null,
    pageNumber: number | null,
    sortKey: number | null,
    createdAt: string
  ): [number, number, number, number] => [
    pdfFileId ? (pieceRank.get(pdfFileId) ?? 1e9) : -1,
    pageNumber ?? 0,
    sortKey ?? 0,
    new Date(createdAt).getTime(),
  ];

  const items: Item[] = useMemo(() => {
    const xs: Item[] = [
      ...filteredAnnotations.map((a): Item => ({ kind: 'annotation', data: a })),
      ...filteredNotes.map((n): Item => ({ kind: 'note', data: n })),
    ];
    xs.sort((a, b) => {
      const ka =
        a.kind === 'annotation'
          ? sortTuple(a.data.pdf_file_id, a.data.page_number, null, a.data.created_at)
          : sortTuple(a.data.pdf_file_id, a.data.page_number, a.data.sort_key, a.data.created_at);
      const kb =
        b.kind === 'annotation'
          ? sortTuple(b.data.pdf_file_id, b.data.page_number, null, b.data.created_at)
          : sortTuple(b.data.pdf_file_id, b.data.page_number, b.data.sort_key, b.data.created_at);
      for (let i = 0; i < ka.length; i += 1) {
        if (ka[i]! !== kb[i]!) return ka[i]! - kb[i]!;
      }
      return 0;
    });
    return xs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAnnotations, filteredNotes, pieceRank]);

  const counters = useMemo(() => {
    const c: Record<AnnotationCategory, number> = {
      favorable: 0,
      defavorable: 0,
      important: 0,
      a_verifier: 0,
    };
    for (const a of annotations) c[a.category] += 1;
    return c;
  }, [annotations]);

  const authors = useMemo(() => {
    const set = new Set<string>();
    for (const a of annotations) if (a.creator_display_name) set.add(a.creator_display_name);
    for (const n of notes) if (n.creator_display_name) set.add(n.creator_display_name);
    return Array.from(set).sort();
  }, [annotations, notes]);

  // ---- Note CRUD --------------------------------------------------------

  const submitDossierWideNote = async (content: string) => {
    if (!content.trim()) return;
    try {
      await api.post<DossierNote>(`/dossiers/${dossierId}/notes`, {
        content: content.trim(),
      });
      setDossierNoteDraft(null);
      await loadNotes();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const updateNote = async (noteId: string, content: string) => {
    await api.patch<DossierNote>(`/notes/${noteId}`, { content });
    await loadNotes();
  };

  const deleteNote = async (noteId: string) => {
    await api.delete(`/notes/${noteId}`);
    await loadNotes();
  };

  /**
   * Drag-drop reorder (Sprint 2-4 / ADR-039) — scope-aware bisect.
   *
   * The dragged note is placed JUST BEFORE the target. New sort_key =
   * (prevSiblingKey + targetKey) / 2 if a previous sibling exists in the
   * same scope, else targetKey - 1.0. Different scope (different
   * pdf_file_id or page_number) → no-op (we don't move notes across pages).
   */
  const reorderNote = async (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    const dragged = notes.find((n) => n.id === draggedId);
    const target = notes.find((n) => n.id === targetId);
    if (!dragged || !target) return;
    if (dragged.pdf_file_id !== target.pdf_file_id || dragged.page_number !== target.page_number) {
      return;
    }
    const siblings = notes
      .filter(
        (n) =>
          n.id !== draggedId &&
          n.pdf_file_id === target.pdf_file_id &&
          n.page_number === target.page_number
      )
      .sort((a, b) => (a.sort_key ?? 0) - (b.sort_key ?? 0));
    const idx = siblings.findIndex((n) => n.id === targetId);
    if (idx < 0) return;
    const targetKey = target.sort_key ?? 0;
    const prev = idx > 0 ? siblings[idx - 1] : null;
    const newKey = prev != null ? ((prev.sort_key ?? 0) + targetKey) / 2 : targetKey - 1.0;
    await api.patch<DossierNote>(`/notes/${draggedId}`, { sort_key: newKey });
    await loadNotes();
  };

  // ---- Group items by piece for visual section headers -----------------

  type Section = { pdfFileId: string | null; items: Item[] };
  const sections: Section[] = useMemo(() => {
    const out: Section[] = [];
    let current: Section | null = null;
    for (const it of items) {
      const pdfId = it.kind === 'annotation' ? it.data.pdf_file_id : it.data.pdf_file_id;
      if (!current || current.pdfFileId !== pdfId) {
        current = { pdfFileId: pdfId, items: [] };
        out.push(current);
      }
      current.items.push(it);
    }
    return out;
  }, [items]);

  // ---- Render -----------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Header counters + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-rule bg-paper-elevated p-3">
        <div className="flex items-center gap-2 text-[12px] text-text-secondary">
          <CounterCircle
            category="favorable"
            count={counters.favorable}
            title="Glisse une annotation ici pour la passer en favorable"
            onDropAnnotation={(id) => updateCategory(id, 'favorable')}
          />
          <CounterCircle
            category="defavorable"
            count={counters.defavorable}
            title="Glisse une annotation ici pour la passer en défavorable"
            onDropAnnotation={(id) => updateCategory(id, 'defavorable')}
          />
          <CounterCircle
            category="important"
            count={counters.important}
            title="Glisse une annotation ici pour la passer en important"
            onDropAnnotation={(id) => updateCategory(id, 'important')}
          />
          <CounterCircle
            category="a_verifier"
            count={counters.a_verifier}
            title="Glisse une annotation ici pour la passer en à vérifier"
            onDropAnnotation={(id) => updateCategory(id, 'a_verifier')}
          />
          <span className="ml-2">
            {annotations.length} annotation{annotations.length > 1 ? 's' : ''} · {notes.length} note
            {notes.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Btn
            variant={selectionMode ? 'dark' : 'ghost'}
            size="sm"
            onClick={() => (selectionMode ? exitSelection() : setSelectionMode(true))}
            title={
              selectionMode
                ? 'Quitter le mode sélection'
                : 'Sélection multiple pour opérations groupées'
            }
          >
            {selectionMode ? '✕ Sortir' : '☐ Sélection'}
          </Btn>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => void exportNotesPdf()}
            disabled={exporting || (annotations.length === 0 && notes.length === 0)}
            title={
              selectionMode && selectedIds.size > 0
                ? `Exporter en PDF les ${selectedIds.size} annotation(s) sélectionnée(s)`
                : 'Exporter en PDF toutes les annotations et notes du dossier'
            }
          >
            {exporting ? 'Export…' : '↓ Exporter PDF'}
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            onClick={() =>
              dossierNoteDraft === null ? setDossierNoteDraft('') : setDossierNoteDraft(null)
            }
          >
            + Note libre dossier
          </Btn>
        </div>
      </div>

      {selectionMode && selectedIds.size > 0 && (
        <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-md border border-accent/40 bg-accent-light px-3 py-2 shadow-sm">
          <span className="text-[12px] font-medium text-accent-text">
            {selectedIds.size} annotation{selectedIds.size > 1 ? 's' : ''} sélectionnée
            {selectedIds.size > 1 ? 's' : ''}
          </span>
          <span className="ml-auto flex flex-wrap items-center gap-1.5">
            <div className="relative">
              <Btn
                variant="secondary"
                size="sm"
                onClick={() => setBulkMenu(bulkMenu === 'category' ? null : 'category')}
                disabled={bulkBusy}
              >
                Catégorie ▾
              </Btn>
              {bulkMenu === 'category' && (
                <ul className="absolute right-0 top-full z-30 mt-1 w-44 overflow-hidden rounded-md border border-rule bg-paper-elevated shadow-md">
                  {(['favorable', 'defavorable', 'important', 'a_verifier'] as const).map((c) => (
                    <li key={c}>
                      <button
                        onClick={() => void bulkSetCategory(c)}
                        disabled={bulkBusy}
                        className={`block w-full px-2.5 py-1.5 text-left text-[11px] font-semibold uppercase tracking-[1px] hover:bg-paper-subtle ${CAT_META[c].labelClass}`}
                      >
                        {CAT_META[c].label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="relative">
              <Btn
                variant="secondary"
                size="sm"
                onClick={() => setBulkMenu(bulkMenu === 'parties' ? null : 'parties')}
                disabled={bulkBusy || parties.length === 0}
              >
                + Partie ▾
              </Btn>
              {bulkMenu === 'parties' && (
                <ul className="absolute right-0 top-full z-30 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-rule bg-paper-elevated shadow-md">
                  {parties.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => void bulkAddParty(p.id)}
                        disabled={bulkBusy}
                        className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-paper-subtle"
                      >
                        <span className="font-medium text-text-primary">
                          {p.prenom ? `${p.prenom} ` : ''}
                          {p.nom}
                        </span>
                        <span className="ml-1.5 text-[10px] uppercase tracking-[0.3px] text-text-tertiary">
                          {ROLE_LABELS_SHORT[p.role]}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="relative">
              <Btn
                variant="secondary"
                size="sm"
                onClick={() => setBulkMenu(bulkMenu === 'tags' ? null : 'tags')}
                disabled={bulkBusy || tags.length === 0}
              >
                + Tag ▾
              </Btn>
              {bulkMenu === 'tags' && (
                <ul className="absolute right-0 top-full z-30 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-rule bg-paper-elevated shadow-md">
                  {tags.map((t) => (
                    <li key={t.id}>
                      <button
                        onClick={() => void bulkAddTag(t.id)}
                        disabled={bulkBusy}
                        className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-paper-subtle"
                      >
                        <span className="font-medium text-text-primary">#{t.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Btn
              variant="secondary"
              size="sm"
              onClick={() => {
                if (window.confirm(`Supprimer ${selectedIds.size} annotation(s) ?`)) {
                  void bulkDelete();
                }
              }}
              disabled={bulkBusy}
              title="Supprimer toutes les annotations sélectionnées"
              className="border-cat-def/40 text-cat-def hover:bg-cat-def-pastel"
            >
              ✕ Supprimer
            </Btn>
          </span>
        </div>
      )}

      {dossierNoteDraft !== null && (
        <div className="space-y-2 rounded-md border border-accent/40 bg-accent-lighter p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[1px] text-accent-text">
            Nouvelle note dossier (sans pièce)
          </div>
          <div className="relative">
            <textarea
              value={dossierNoteDraft}
              onChange={(e) => setDossierNoteDraft(e.target.value)}
              rows={3}
              autoFocus
              placeholder="Note libre — raisonnement transverse, à retrouver depuis l'export Word…"
              className="w-full rounded-sm border border-rule-strong bg-paper-elevated px-2 py-1.5 pr-10 text-[13px]"
            />
            <div className="absolute top-1.5 right-1.5">
              <DictationButton
                size="sm"
                onTranscript={(chunk) =>
                  setDossierNoteDraft((prev) => ((prev ?? '') ? prev + ' ' : '') + chunk)
                }
                title="Dicter la note"
              />
            </div>
          </div>
          <div className="flex justify-end gap-1.5">
            <Btn variant="ghost" size="sm" onClick={() => setDossierNoteDraft(null)}>
              Annuler
            </Btn>
            <Btn
              variant="primary"
              size="sm"
              onClick={() => void submitDossierWideNote(dossierNoteDraft ?? '')}
              disabled={!dossierNoteDraft.trim()}
            >
              Créer la note
            </Btn>
          </div>
        </div>
      )}

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Rechercher dans les extraits, commentaires, notes…"
        className="w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm placeholder:text-text-tertiary focus:border-accent focus:outline-none"
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilterStale7d((v) => !v)}
          className={`rounded-md border px-3 py-1.5 text-[12px] transition ${
            filterStale7d
              ? 'border-cat-imp bg-cat-imp-pastel text-cat-imp-text font-medium'
              : 'border-rule-strong bg-paper-elevated text-text-secondary hover:bg-paper-card'
          }`}
          title="Annotations non touchées depuis plus de 7 jours — à relire"
        >
          🕐 {filterStale7d ? 'Désactiver' : 'Non rouvertes >7j'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value as AnnotationCategory | 'all')}
          className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm"
        >
          <option value="all">Toutes catégories ({annotations.length})</option>
          <option value="favorable">Favorable ({counters.favorable})</option>
          <option value="defavorable">Défavorable ({counters.defavorable})</option>
          <option value="important">Important ({counters.important})</option>
          <option value="a_verifier">À vérifier ({counters.a_verifier})</option>
        </select>
        <select
          value={filterParty}
          onChange={(e) => setFilterParty(e.target.value)}
          className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm"
          disabled={parties.length === 0}
        >
          <option value="all">Toutes parties ({parties.length})</option>
          {parties.length > 0 && <option value="__none__">— sans partie —</option>}
          {parties.map((p) => (
            <option key={p.id} value={p.id}>
              {[p.prenom, p.nom].filter(Boolean).join(' ')} · {ROLE_LABELS_SHORT[p.role]}
              {p.is_client ? ' (client)' : ''}
            </option>
          ))}
        </select>
        <select
          value={filterPdfId}
          onChange={(e) => setFilterPdfId(e.target.value)}
          className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm"
        >
          <option value="all">Toutes pièces greffe ({pdfs.length})</option>
          {[...pdfs]
            .sort((a, b) => (pieceRank.get(a.id) ?? 0) - (pieceRank.get(b.id) ?? 0))
            .map((p) => (
              <option key={p.id} value={p.id}>
                {pieceLabel(p)}
              </option>
            ))}
        </select>
        {piecesClient.length > 0 && (
          <select
            value={filterPieceClientId}
            onChange={(e) => setFilterPieceClientId(e.target.value)}
            className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm"
          >
            <option value="all">Toutes pièces client ({piecesClient.length})</option>
            {piecesClient.map((p) => (
              <option key={p.id} value={p.id}>
                {p.titre}
                {p.date_piece ? ` · ${p.date_piece}` : ''}
              </option>
            ))}
          </select>
        )}
        <select
          value={filterAuthor}
          onChange={(e) => setFilterAuthor(e.target.value)}
          className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm"
        >
          <option value="all">Tous auteurs</option>
          {authors.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
          className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm"
          disabled={tags.length === 0}
        >
          <option value="all">Tous tags ({tags.length})</option>
          {tags.length > 0 && <option value="__none__">— sans tag —</option>}
          {tags.map((t) => (
            <option key={t.id} value={t.id}>
              #{t.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-text-secondary">Chargement…</p>
      ) : items.length === 0 ? (
        <p className="rounded-md border border-dashed border-rule-strong bg-paper-card p-10 text-center text-sm text-text-secondary">
          Aucune annotation ni note. Ouvre une pièce et sélectionne du texte pour démarrer.
        </p>
      ) : (
        <div className="space-y-5">
          {sections.map((sec, sIdx) => {
            const pdf = sec.pdfFileId ? pdfById.get(sec.pdfFileId) : undefined;
            return (
              <section key={`sec-${sec.pdfFileId ?? 'dossier'}-${sIdx}`} className="space-y-2">
                <header className="sticky top-0 z-10 flex items-baseline justify-between gap-3 border-b border-rule-strong bg-paper py-1.5">
                  <h3 className="font-serif text-[15px] font-medium text-text-primary">
                    {sec.pdfFileId ? pieceLabel(pdf) : 'Notes dossier (sans pièce)'}
                  </h3>
                  {sec.pdfFileId && pdf && (
                    <Link
                      to={`/dossiers/${dossierId}/pieces/${pdf.id}`}
                      className="font-mono text-[11px] text-text-link hover:underline"
                    >
                      ouvrir →
                    </Link>
                  )}
                </header>
                <ul className="space-y-2">
                  {sec.items.map((it) => (
                    <li key={`${it.kind}-${it.data.id}`}>
                      {it.kind === 'annotation' ? (
                        <AnnotationItem
                          annotation={it.data}
                          pdf={pdf}
                          dossierId={dossierId}
                          parties={parties}
                          partyById={partyById}
                          tags={tags}
                          tagById={tagById}
                          selectionMode={selectionMode}
                          selected={selectedIds.has(it.data.id)}
                          onToggleSelect={() => toggleSelected(it.data.id)}
                          onDelete={async () => {
                            await api.delete(`/annotations/${it.data.id}`);
                            await onAnnotationsChange();
                          }}
                          onPartyLinksChange={(ids) => updatePartyLinks(it.data.id, ids)}
                          onTagLinksChange={(ids) => updateTagLinks(it.data.id, ids)}
                          onCreateAndLinkTag={(label, color) =>
                            createAndLinkTag(it.data.id, label, color)
                          }
                          onCommentChange={(c) => updateComment(it.data.id, c)}
                          onCategoryChange={(c) => updateCategory(it.data.id, c)}
                        />
                      ) : (
                        <NoteItem
                          note={it.data}
                          pdf={pdf}
                          onUpdate={(c) => updateNote(it.data.id, c)}
                          onDelete={() => deleteNote(it.data.id)}
                          onReorder={reorderNote}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AnnotationItem({
  annotation: a,
  pdf,
  dossierId,
  parties,
  partyById,
  tags,
  tagById,
  selectionMode,
  selected,
  onToggleSelect,
  onDelete,
  onPartyLinksChange,
  onTagLinksChange,
  onCreateAndLinkTag,
  onCommentChange,
  onCategoryChange,
}: {
  annotation: Annotation;
  pdf: PdfMetadata | undefined;
  dossierId: string;
  parties: Partie[];
  partyById: Map<string, Partie>;
  tags: Tag[];
  tagById: Map<string, Tag>;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onDelete: () => Promise<void>;
  onPartyLinksChange: (partyIds: string[]) => Promise<void>;
  onTagLinksChange: (tagIds: string[]) => Promise<void>;
  onCreateAndLinkTag: (label: string, color?: string) => Promise<string | null>;
  onCommentChange: (newComment: string) => Promise<void>;
  onCategoryChange: (newCategory: AnnotationCategory) => Promise<void>;
}) {
  const meta = CAT_META[a.category];
  const initials = toInitials(a.creator_display_name);
  const cotation: string[] = [];
  if (a.ref_carton) cotation.push(`Carton ${a.ref_carton}`);
  if (a.ref_sous_farde) cotation.push(`farde ${a.ref_sous_farde}`);
  if (a.ref_piece) cotation.push(`pièce ${a.ref_piece}`);
  const cotationStr = cotation.join(' · ');

  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [partyMenuOpen, setPartyMenuOpen] = useState(false);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const [newTagDraft, setNewTagDraft] = useState<string | null>(null);
  const [newTagColor, setNewTagColor] = useState<string>('');
  const [editingComment, setEditingComment] = useState(false);
  const [draftComment, setDraftComment] = useState(a.comment ?? '');
  const [catMenuOpen, setCatMenuOpen] = useState(false);

  const linkedTags = a.linked_tag_ids
    .map((id) => tagById.get(id))
    .filter((t): t is Tag => Boolean(t));
  const unlinkedTags = tags.filter((t) => !a.linked_tag_ids.includes(t.id));

  const toggleTagLink = async (tagId: string) => {
    const next = a.linked_tag_ids.includes(tagId)
      ? a.linked_tag_ids.filter((x) => x !== tagId)
      : [...a.linked_tag_ids, tagId];
    await onTagLinksChange(next);
  };

  const saveComment = async () => {
    setBusy(true);
    try {
      await onCommentChange(draftComment.trim());
      setEditingComment(false);
    } finally {
      setBusy(false);
    }
  };

  const linkedParties = a.linked_party_ids
    .map((id) => partyById.get(id))
    .filter((p): p is Partie => Boolean(p));
  const unlinkedParties = parties.filter((p) => !a.linked_party_ids.includes(p.id));

  const togglePartyLink = async (partyId: string) => {
    const next = a.linked_party_ids.includes(partyId)
      ? a.linked_party_ids.filter((x) => x !== partyId)
      : [...a.linked_party_ids, partyId];
    await onPartyLinksChange(next);
  };

  // Tooltip riche au survol — extrait + commentaire + auteur + date.
  const hoverTitle = (() => {
    const parts: string[] = [];
    parts.push(`« ${a.text_extract} »`);
    if (a.comment) parts.push(`\n\nCommentaire : ${a.comment}`);
    parts.push(
      `\n\n${a.creator_display_name ?? '—'} · ${new Date(a.created_at).toLocaleString('fr-BE')}`
    );
    if (cotationStr) parts.push(`\n${cotationStr}`);
    return parts.join('');
  })();

  return (
    <div
      title={hoverTitle}
      draggable
      onDragStart={(e) => {
        // U8 — drag d'une carte annotation sur une pastille de catégorie
        // change sa catégorie. dataTransfer porte l'UUID via un MIME perso.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/x-adr-annotation-id', a.id);
      }}
      className={`group cursor-grab rounded-sm border-l-4 bg-paper-elevated p-3.5 active:cursor-grabbing hover:bg-paper-card ${meta.cardClass} ${selected ? 'ring-2 ring-accent ring-offset-1' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          {selectionMode && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              className="mt-[3px] h-3.5 w-3.5 cursor-pointer rounded-sm border-rule-strong text-accent focus:ring-accent"
              aria-label="Sélectionner cette annotation"
            />
          )}
          <div className="relative">
            <button
              onClick={() => setCatMenuOpen((v) => !v)}
              className={`text-[10px] font-semibold uppercase tracking-[1px] hover:underline ${meta.labelClass}`}
              title="Changer la catégorie"
            >
              {meta.label} ▾
            </button>
            {catMenuOpen && (
              <ul className="absolute left-0 top-full z-20 mt-1 w-44 overflow-hidden rounded-md border border-rule bg-paper-elevated shadow-md">
                {(['favorable', 'defavorable', 'important', 'a_verifier'] as const).map((cat) => (
                  <li key={cat}>
                    <button
                      onClick={async () => {
                        setCatMenuOpen(false);
                        if (cat !== a.category) await onCategoryChange(cat);
                      }}
                      className={`block w-full px-2.5 py-1.5 text-left text-[11px] font-semibold uppercase tracking-[1px] hover:bg-paper-subtle ${
                        cat === a.category ? 'bg-paper-subtle' : ''
                      } ${CAT_META[cat].labelClass}`}
                    >
                      {CAT_META[cat].label}
                      {cat === a.category && <span className="ml-1 text-text-tertiary">·</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Avatar
            size="sm"
            initials={initials}
            title={a.creator_display_name ?? 'auteur inconnu'}
          />
          {confirmDel ? (
            <span className="flex items-center gap-1 text-[11px]">
              <button
                onClick={() => setConfirmDel(false)}
                className="rounded px-1.5 py-0.5 text-text-tertiary hover:bg-paper-subtle"
                disabled={busy}
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  setBusy(true);
                  await onDelete();
                }}
                disabled={busy}
                className="rounded-md bg-cat-def px-2 py-0.5 text-[11px] font-medium text-white"
              >
                Supprimer
              </button>
            </span>
          ) : (
            <span className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
              {!editingComment && (
                <button
                  onClick={() => {
                    setDraftComment(a.comment ?? '');
                    setEditingComment(true);
                  }}
                  className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
                  title={a.comment ? 'Modifier le commentaire' : 'Ajouter un commentaire'}
                >
                  ✎
                </button>
              )}
              <button
                onClick={() => setConfirmDel(true)}
                className="rounded p-1 text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def"
                title="Supprimer"
              >
                ✕
              </button>
            </span>
          )}
        </div>
      </div>
      <p className="mt-1.5 font-serif text-[13px] italic leading-snug text-text-primary">
        « {a.text_extract} »
      </p>
      {editingComment ? (
        <div className="mt-2 space-y-2">
          <div className="relative">
            <textarea
              value={draftComment}
              onChange={(e) => setDraftComment(e.target.value)}
              rows={2}
              autoFocus
              className="w-full rounded-sm border border-rule-strong bg-paper-card px-2 py-1.5 pr-10 text-[12px]"
              placeholder="Commentaire…"
            />
            <div className="absolute top-1.5 right-1.5">
              <DictationButton
                size="sm"
                onTranscript={(chunk) =>
                  setDraftComment((prev) => (prev ? prev + ' ' : '') + chunk)
                }
                title="Dicter le commentaire"
              />
            </div>
          </div>
          <div className="flex justify-end gap-1">
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraftComment(a.comment ?? '');
                setEditingComment(false);
              }}
              disabled={busy}
            >
              Annuler
            </Btn>
            <Btn variant="primary" size="sm" onClick={() => void saveComment()} disabled={busy}>
              {busy ? '…' : 'Enregistrer'}
            </Btn>
          </div>
        </div>
      ) : (
        a.comment && <p className="mt-1 text-[12px] italic text-text-secondary">{a.comment}</p>
      )}

      {/* Party chips — Sprint 2-2 / ADR-036/037 */}
      {(linkedParties.length > 0 || parties.length > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {linkedParties.map((p) => (
            <button
              key={p.id}
              onClick={() => void togglePartyLink(p.id)}
              className={`group/chip inline-flex items-center gap-1 rounded-pill px-2 py-[2px] text-[10px] font-medium ${
                p.is_client
                  ? 'bg-accent-light text-accent-text'
                  : 'bg-paper-subtle text-text-secondary'
              } hover:bg-cat-def-pastel hover:text-cat-def`}
              title={`${ROLE_LABELS_SHORT[p.role]} — cliquer pour délier`}
            >
              <span>
                {p.prenom ? `${p.prenom[0]!}. ` : ''}
                {p.nom}
              </span>
              <span className="opacity-0 transition group-hover/chip:opacity-100">✕</span>
            </button>
          ))}
          {unlinkedParties.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setPartyMenuOpen((v) => !v)}
                className="rounded-pill border border-dashed border-rule-strong bg-paper-elevated px-2 py-[2px] text-[10px] font-medium text-text-tertiary hover:border-accent hover:text-accent-text"
                title="Lier une partie à cet extrait"
              >
                + partie
              </button>
              {partyMenuOpen && (
                <ul className="absolute left-0 top-full z-20 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-rule bg-paper-elevated shadow-md">
                  {unlinkedParties.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={async () => {
                          setPartyMenuOpen(false);
                          await togglePartyLink(p.id);
                        }}
                        className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-paper-subtle"
                      >
                        <span className="font-medium text-text-primary">
                          {p.prenom ? `${p.prenom} ` : ''}
                          {p.nom}
                        </span>
                        <span className="ml-1.5 text-[10px] uppercase tracking-[0.3px] text-text-tertiary">
                          {ROLE_LABELS_SHORT[p.role]}
                        </span>
                        {p.is_client && (
                          <span className="ml-1.5 text-[10px] font-semibold text-accent-text">
                            client
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tag chips — ADR-045 */}
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {linkedTags.map((t) => (
          <button
            key={t.id}
            onClick={() => void toggleTagLink(t.id)}
            className="group/chip inline-flex items-center gap-1 rounded-pill border border-rule-strong bg-paper-card px-2 py-[2px] text-[10px] font-medium text-text-primary hover:bg-cat-def-pastel hover:text-cat-def hover:border-cat-def/40"
            style={
              t.color
                ? { backgroundColor: t.color, color: '#fff', borderColor: 'transparent' }
                : undefined
            }
            title="Cliquer pour délier ce tag"
          >
            <span>#{t.label}</span>
            <span className="opacity-0 transition group-hover/chip:opacity-100">✕</span>
          </button>
        ))}
        {(unlinkedTags.length > 0 || tags.length === 0) && (
          <div className="relative">
            <button
              onClick={() => {
                setTagMenuOpen((v) => !v);
                setNewTagDraft(null);
              }}
              className="rounded-pill border border-dashed border-rule-strong bg-paper-elevated px-2 py-[2px] text-[10px] font-medium text-text-tertiary hover:border-accent hover:text-accent-text"
              title="Ajouter un tag à cet extrait"
            >
              + tag
            </button>
            {tagMenuOpen && (
              <div className="absolute left-0 top-full z-20 mt-1 w-56 overflow-hidden rounded-md border border-rule bg-paper-elevated shadow-md">
                <ul className="max-h-40 overflow-y-auto">
                  {unlinkedTags.map((t) => (
                    <li key={t.id}>
                      <button
                        onClick={async () => {
                          setTagMenuOpen(false);
                          await toggleTagLink(t.id);
                        }}
                        className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-paper-subtle"
                      >
                        <span className="font-medium text-text-primary">#{t.label}</span>
                      </button>
                    </li>
                  ))}
                  {unlinkedTags.length === 0 && (
                    <li className="px-2.5 py-1.5 text-[11px] italic text-text-tertiary">
                      Aucun tag existant.
                    </li>
                  )}
                </ul>
                {newTagDraft === null ? (
                  <button
                    onClick={() => setNewTagDraft('')}
                    className="block w-full border-t border-rule px-2.5 py-1.5 text-left text-[11px] text-accent-text hover:bg-paper-subtle"
                  >
                    + Créer un tag…
                  </button>
                ) : (
                  <div className="space-y-1.5 border-t border-rule p-2">
                    <div className="flex items-center gap-1.5">
                      <input
                        autoFocus
                        value={newTagDraft}
                        onChange={(e) => setNewTagDraft(e.target.value)}
                        placeholder="ex. argument-clé"
                        className="flex-1 rounded-sm border border-rule-strong bg-paper-elevated px-2 py-1 text-[12px]"
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && newTagDraft && newTagDraft.trim()) {
                            e.preventDefault();
                            const label = newTagDraft.trim();
                            const color = newTagColor || undefined;
                            setNewTagDraft(null);
                            setNewTagColor('');
                            setTagMenuOpen(false);
                            await onCreateAndLinkTag(label, color);
                          } else if (e.key === 'Escape') {
                            setNewTagDraft(null);
                          }
                        }}
                      />
                      <input
                        type="color"
                        value={newTagColor || '#cbd5f5'}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="h-6 w-7 cursor-pointer rounded-sm border border-rule-strong"
                        title="Couleur du tag (optionnel)"
                      />
                    </div>
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setNewTagDraft(null)}
                        className="rounded px-1.5 py-0.5 text-[11px] text-text-tertiary hover:bg-paper-subtle"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={async () => {
                          if (newTagDraft && newTagDraft.trim()) {
                            const label = newTagDraft.trim();
                            const color = newTagColor || undefined;
                            setNewTagDraft(null);
                            setNewTagColor('');
                            setTagMenuOpen(false);
                            await onCreateAndLinkTag(label, color);
                          }
                        }}
                        disabled={!newTagDraft || !newTagDraft.trim()}
                        className="rounded bg-accent-light px-2 py-0.5 text-[11px] font-medium text-accent-text disabled:opacity-50"
                      >
                        Créer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Référence pièce — doit être très visible (CdC §2 / ADR-032) */}
      <div className="mt-2 rounded-sm bg-paper-subtle px-2.5 py-1.5 font-mono text-[11px] leading-snug text-text-secondary">
        <Link
          to={`/dossiers/${dossierId}/pieces/${a.pdf_file_id}?annotation=${a.id}${
            a.page_number ? `&page=${a.page_number}` : ''
          }`}
          className="font-medium text-text-primary hover:underline"
          title="Ouvrir la pièce à cette page"
        >
          {pieceRefLine(pdf, a.page_number)}
        </Link>
        {cotationStr && <span className="ml-2 italic text-text-tertiary">{cotationStr}</span>}
        <span className="ml-2 text-text-tertiary">· {relativeAge(a.created_at)}</span>
      </div>
    </div>
  );
}

function NoteItem({
  note,
  pdf,
  onUpdate,
  onDelete,
  onReorder,
}: {
  note: DossierNote;
  pdf: PdfMetadata | undefined;
  onUpdate: (content: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onReorder: (draggedId: string, targetId: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const initials = toInitials(note.creator_display_name);

  const save = async () => {
    setBusy(true);
    try {
      await onUpdate(draft.trim());
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      draggable={!editing}
      onDragStart={(e) => {
        if (editing) return;
        e.dataTransfer.setData('text/plain', note.id);
        // Encode source scope dans un type MIME custom (lisible par le target
        // pendant dragover, sans accès au getData pour des raisons de sécurité).
        e.dataTransfer.setData(noteScopeMime(note), '');
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('text/plain')) return;
        if (!e.dataTransfer.types.includes(noteScopeMime(note))) {
          // Scope différent (autre pièce / autre page) — refus visuel.
          e.dataTransfer.dropEffect = 'none';
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const draggedId = e.dataTransfer.getData('text/plain');
        if (draggedId && draggedId !== note.id) {
          void onReorder(draggedId, note.id);
        }
      }}
      className={`group rounded-md border border-dashed border-rule-strong bg-accent-lighter p-3.5 transition ${
        dragOver ? 'border-accent ring-2 ring-accent/40' : ''
      } cursor-grab active:cursor-grabbing`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[1px] text-accent-text">
          NOTE LIBRE
        </div>
        <div className="flex items-center gap-2">
          <Avatar
            size="sm"
            initials={initials}
            title={note.creator_display_name ?? 'auteur inconnu'}
          />
          {confirmDel ? (
            <span className="flex items-center gap-1 text-[11px]">
              <button
                onClick={() => setConfirmDel(false)}
                disabled={busy}
                className="rounded px-1.5 py-0.5 text-text-tertiary hover:bg-paper-subtle"
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  setBusy(true);
                  await onDelete();
                }}
                disabled={busy}
                className="rounded-md bg-cat-def px-2 py-0.5 text-[11px] font-medium text-white"
              >
                Supprimer
              </button>
            </span>
          ) : !editing ? (
            <span className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
              <button
                onClick={() => {
                  setDraft(note.content);
                  setEditing(true);
                }}
                className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
                title="Modifier"
              >
                ✎
              </button>
              <button
                onClick={() => setConfirmDel(true)}
                className="rounded p-1 text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def"
                title="Supprimer"
              >
                ✕
              </button>
            </span>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="mt-2 space-y-2">
          <div className="relative">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              autoFocus
              className="w-full rounded-sm border border-rule-strong bg-paper-elevated px-2 py-1.5 pr-10 text-[13px]"
            />
            <div className="absolute top-1.5 right-1.5">
              <DictationButton
                size="sm"
                onTranscript={(chunk) => setDraft((prev) => (prev ? prev + ' ' : '') + chunk)}
                title="Dicter la note"
              />
            </div>
          </div>
          <div className="flex justify-end gap-1">
            <Btn variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>
              Annuler
            </Btn>
            <Btn variant="primary" size="sm" onClick={() => void save()} disabled={busy}>
              {busy ? '…' : 'Enregistrer'}
            </Btn>
          </div>
        </div>
      ) : (
        <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-text-primary">
          {note.content}
        </p>
      )}

      <div className="mt-2 rounded-sm bg-paper-subtle/60 px-2.5 py-1 font-mono text-[11px] text-text-tertiary">
        {note.pdf_file_id ? pieceRefLine(pdf, note.page_number) : 'Note dossier (sans pièce)'}
        <span className="ml-2">· {relativeAge(note.created_at)}</span>
      </div>
    </div>
  );
}
